// eslint-disable-next-line prettier/prettier
import type { } from "@mui/lab/themeAugmentation";
import { ThemeOptions } from "@mui/material";
// eslint-disable-next-line prettier/prettier
import type { } from "@mui/x-charts/themeAugmentation";
// eslint-disable-next-line prettier/prettier
import type { } from "@mui/x-data-grid/themeAugmentation";

// Extend the ButtonPropsSizeOverrides to include 'xs'
declare module "@mui/material/Button" {
  interface ButtonPropsSizeOverrides {
    xs: true;
  }
}

export const components: ThemeOptions["components"] = {
  MuiCssBaseline: {
    styleOverrides: (theme) => `
      pre {
        margin: 0;
        padding: ${theme.spacing(1)};
        background-color: ${theme.palette.grey[100]};
      }
      `,
  },
  MuiInput: {
    defaultProps: {
      size: "small",
    },
  },
  MuiTextField: {
    defaultProps: {
      size: "small",
      variant: "outlined",
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
      }),
    },
  },
  MuiTooltip: {
    defaultProps: {
      enterDelay: 1000,
      enterNextDelay: 500,
      leaveDelay: 200,
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& fieldset": { borderColor: theme.palette.text.primary },
      }),
    },
  },
  MuiBarChart: {
    defaultProps: {
      borderRadius: 4,
    },
  },
  MuiFilledInput: {
    defaultProps: {
      size: "small",
    },
  },
  MuiChip: {
    defaultProps: { size: "small" },
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: "12px",
        textTransform: "uppercase",
        color: theme.palette.common.black,
        "&.MuiChip-colorDefault": {
          backgroundColor: theme.palette.pastel.default.main,
        },
        "&.MuiChip-colorInfo": {
          backgroundColor: theme.palette.pastel.info.main,
        },
        "&.MuiChip-colorWarning": {
          backgroundColor: theme.palette.pastel.warning.main,
        },
        "&.MuiChip-colorError": {
          backgroundColor: theme.palette.pastel.error.main,
        },
        "&.MuiChip-colorSuccess": {
          backgroundColor: theme.palette.pastel.success.main,
        },
        "& .MuiChip-icon": {
          color: theme.palette.common.black,
        },
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      size: "small",
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      size: "small",
      variant: "outlined",
    },
  },
  MuiCardHeader: {
    defaultProps: {
      subheaderTypographyProps: { variant: "body2" },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&:last-child": {
          paddingBottom: theme.spacing(2),
        },
      }),
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2),
      }),
    },
  },
  MuiTypography: { defaultProps: { variant: "body2" } },
  MuiStack: {
    defaultProps: {
      useFlexGap: true,
    },
    styleOverrides: {
      root: {
        marginTop: 0,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: `0px ${theme.spacing(1)} 8px 0px`,
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      size: "small",
      variant: "contained",
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...(ownerState.size === "xs" && {
          padding: "4px 6px",
          height: "28px",
          fontSize: theme.typography.caption.fontSize,
          lineHeight: 1,
          "& .MuiButton-startIcon": {
            marginLeft: -2,
            marginRight: theme.spacing(0.4),
          },
          "& svg": {
            height: "18px",
            width: "18px",
          },
        }),
      }),
    },
  },
  MuiLoadingButton: {
    defaultProps: {
      size: "small",
      variant: "contained",
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2),
      }),
    },
  },
  MuiDialogActions: {
    defaultProps: {
      disableSpacing: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2),
        gap: theme.spacing(2),
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(0),
        paddingTop: theme.spacing(0),
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: "32px",
      },
    },
  },
  MuiDataGrid: {
    defaultProps: {
      disableVirtualization: true,
      autosizeOptions: {
        includeHeaders: true,
        includeOutliers: true,
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ["--DataGrid-containerBackground"]: `${theme.palette.background.paper} !important`,
        "& .MuiDataGrid-cellEmpty": {
          background: `${theme.palette.background.paper} !important`,
        },
      }),
    },
  },
};
